<template>
  <div
    :class="[
      mobile ? 'app-sidebar app-sidebar--mobile' : 'app-sidebar',
      { 'app-sidebar--collapsed': collapsed && !mobile },
    ]"
  >
    <RouterLink :to="brandUrl" class="app-sidebar__brand" @click="emitNavigate">
      <img
        v-if="collapsed && !mobile"
        class="app-sidebar__brand-mark"
        src="../assets/givvy.png"
        alt="Givaroo"
      />
      <img
        v-else
        class="app-sidebar__brand-logo"
        src="../assets/logo-white-wide-small.png"
        alt="Givaroo"
      />
    </RouterLink>

    <nav class="app-sidebar__nav" aria-label="Main navigation">
      <p v-if="!collapsed || mobile" class="app-sidebar__section-label">Menu</p>
      <ul role="list" class="space-y-0.5">
        <li v-for="item in items" :key="item.url">
          <a
            v-if="item.external"
            :href="item.url"
            target="_blank"
            rel="noopener"
            class="app-sidebar-link"
            :title="collapsed && !mobile ? item.name : undefined"
            @click="emitNavigate"
          >
            <span class="app-sidebar-link__icon">
              <component :is="item.icon" class="h-4 w-4" aria-hidden="true" />
            </span>
            <span class="app-sidebar-link__label">{{ item.name }}</span>
            <ArrowTopRightOnSquareIcon
              class="app-sidebar-link__label ml-auto h-3.5 w-3.5 opacity-70"
              aria-label="Opens in a new tab"
            />
          </a>
          <RouterLink
            v-else
            :to="item.url"
            :class="linkClass(item.url)"
            :title="collapsed && !mobile ? item.name : undefined"
            @click="emitNavigate"
          >
            <span class="app-sidebar-link__icon">
              <component :is="item.icon" class="h-4 w-4" aria-hidden="true" />
            </span>
            <span class="app-sidebar-link__label">{{ item.name }}</span>
            <span class="app-sidebar-link__indicator" aria-hidden="true" />
          </RouterLink>
        </li>
      </ul>
    </nav>

    <!-- Pinned below the scrolling menu; these open the assistant panel / bug report form. -->
    <div v-if="helpItems.length" class="app-sidebar__help">
      <p v-if="!collapsed || mobile" class="app-sidebar__section-label">Help</p>
      <ul role="list" class="space-y-0.5">
        <li v-for="item in helpItems" :key="item.name">
          <button
            type="button"
            class="app-sidebar-link w-full text-left"
            :title="collapsed && !mobile ? item.name : undefined"
            aria-haspopup="dialog"
            @click="openHelpItem(item)"
          >
            <span class="app-sidebar-link__icon">
              <component :is="item.icon" class="h-4 w-4" aria-hidden="true" />
            </span>
            <span class="app-sidebar-link__label">{{ item.name }}</span>
          </button>
        </li>
      </ul>
    </div>
  </div>
</template>

<script setup>
import { defineEmits, defineProps } from 'vue'
import { useRoute } from 'vue-router'
import { ArrowTopRightOnSquareIcon } from '@heroicons/vue/20/solid'

defineProps({
  items: {
    type: Array,
    required: true,
  },
  mobile: {
    type: Boolean,
    default: false,
  },
  collapsed: {
    type: Boolean,
    default: false,
  },
  brandUrl: {
    type: String,
    default: '/campaigns',
  },
  // [{ name, icon, open }] -- actions rather than routes, shown under the main menu.
  helpItems: {
    type: Array,
    default: () => [],
  },
})

const emit = defineEmits(['navigate'])

const route = useRoute()

const isActiveRoute = (url) => {
  if (url === '/dashboard') {
    return route.path === '/dashboard'
  }
  return route.path === url || route.path.startsWith(`${url}/`)
}

const linkClass = (url) => {
  const active = isActiveRoute(url)
  return ['app-sidebar-link', { 'app-sidebar-link--active': active }]
}

const emitNavigate = () => {
  emit('navigate')
}

const openHelpItem = (item) => {
  // Closes the mobile slide-out first so the panel / form isn't opened behind it.
  emit('navigate')
  item.open()
}
</script>
